import { hasLdtValidationErrors } from '@ldt/features/orders/utils/has_ldt_validation_errors';
import type {
  CheckOrderValidationErrorsContext,
  GetAvailableActionsContext,
  LdtOrderAction,
} from '@ldt/workflows/types';
import { LdtOrderAction as Action } from '@ldt/workflows/types';
import { LdtOrderDetailFieldsFragment, OrderStatus } from 'src/types';
import { WorkflowType } from 'src/types/workflows';
import {
  isCustomerSuccess,
  isCustomerSuccessOrSalesUser,
  isLabTech,
  isSalesUser,
} from 'src/utils';

function getDraftActions(): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  if (isLabTech()) {
    actions.push(Action.EditOrder);
  }
  if (isCustomerSuccessOrSalesUser()) {
    actions.push(Action.EditOrder);
    actions.push(Action.Cancel);
  }

  return actions;
}

function getQcPendingActions(): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  if (isCustomerSuccessOrSalesUser()) {
    actions.unshift(Action.UpdateFiles);
    actions.push(Action.EditOrder);
    actions.push(Action.Cancel);
  }

  return actions;
}

function getQcApprovedActions(): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  if (isLabTech()) {
    actions.push(Action.EditOrder);
  } else if (isCustomerSuccessOrSalesUser()) {
    actions.push(Action.UpdateFiles);
    actions.push(Action.EditOrder);
    actions.push(Action.Cancel);
  }

  return actions;
}

function getLabProcessingActions(): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  if (isCustomerSuccessOrSalesUser()) {
    actions.push(Action.UpdateFiles);
    actions.push(Action.Cancel);
  }

  return actions;
}

function getAiPipelineActions(): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  if (isCustomerSuccessOrSalesUser()) {
    actions.push(Action.UpdateFiles);
  }

  return actions;
}

function getReportPendingActions(): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  if (isCustomerSuccessOrSalesUser()) {
    actions.push(Action.UpdateFiles);
    actions.push(Action.Cancel);
  }

  return actions;
}

function getReportReadyActions(): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  if (isCustomerSuccess()) {
    actions.push(Action.DisplayPatientReport);
    actions.push(Action.UpdateFiles);
    actions.push(Action.ReviseReportMutation);
  } else if (isSalesUser()) {
    actions.push(Action.DisplayDeIdentifiedReport);
    actions.push(Action.UpdateFiles);
    actions.push(Action.ReviseReportMutation);
  }

  return actions;
}

export function getAvailableActions(
  ctx: GetAvailableActionsContext,
): LdtOrderAction[] {
  const { order } = ctx;
  const status = order.status;

  if (status === OrderStatus.Draft) {
    return getDraftActions();
  } else if (status === OrderStatus.QualityControlPending) {
    return getQcPendingActions();
  } else if (status === OrderStatus.QualityControlApproved) {
    return getQcApprovedActions();
  } else if (status === OrderStatus.LabProcessing) {
    return getLabProcessingActions();
  } else if (
    status === OrderStatus.AiProcessing ||
    status === OrderStatus.AiError
  ) {
    return getAiPipelineActions();
  } else if (status === OrderStatus.ReportPending) {
    return getReportPendingActions();
  } else if (status === OrderStatus.ReportReady) {
    return getReportReadyActions();
  }

  return [];
}

// Not used for Tempus workflow.
export function getOrderMissingInfoMessages(
  _order: LdtOrderDetailFieldsFragment,
): string[] {
  const messages: string[] = [];

  return messages;
}

export async function checkOrderValidationErrors(
  context: CheckOrderValidationErrorsContext,
): Promise<boolean> {
  return hasLdtValidationErrors(
    context.order,
    isCustomerSuccessOrSalesUser(),
    WorkflowType.TEMPUS,
    context.order.status === OrderStatus.QualityControlPending,
    context.order.specimenType,
  );
}
